// Rules are a frontend-only grouping; the backend receives a flat node list. Each rule's
// nodes are namespaced by its 1-based number behind an "r." prefix (e.g. "r.1.SMA.short",
// "r.2.EMA.long"). Enabled optimisations are namespaced similarly behind an "o." prefix
// (e.g. "o.1.PRICE.TREND").
//
// The live cap is per-account (`accountDetails.strategyMaxRules`); this is only the fallback
// for load payloads that predate the field.
export const DEFAULT_MAX_RULES = 5;

// A rule selects one combination (any number of indicators) via a cascade of pickers. `codes`
// holds the indicators chosen so far, in the order they were picked — position carries no meaning,
// the matching combination is the one holding exactly this set (combinationFor). Indicator codes
// are unique within every combination, so `nodes` / `editOpen` are safely keyed by code.
export type RuleState = {
    codes: string[];
    nodes: Record<string, Record<string, unknown>>;
    editOpen: Record<string, boolean>;
};

export const emptyRule = (): RuleState => ({ codes: [], nodes: {}, editOpen: {} });

// An enabled optimisation's editable state: its indicators are fixed (opt.indicatorInstances), so
// there is no picker — just the per-indicator node values (keyed by code) and which param panels are
// open. Presence of a state for an optimisation index means its checkbox is ticked.
export type OptimisationState = {
    nodes: Record<string, Record<string, unknown>>;
    editOpen: Record<string, boolean>;
};

// Picker value meaning "stop the cascade here" — finalize the smaller combination matching the
// current selection even though larger combinations also contain it (e.g. BOLL + PRICE.CLOSE).
export const NONE_SENTINEL = '__NONE__';

// True when every code in `needed` appears somewhere in `arr`, at any position.
export const containsAll = (needed: string[], arr: string[]): boolean => needed.every((code) => arr.includes(code));

// True when both lists hold the same codes in any order. Codes are unique within a combination and
// within a rule, so comparing as sets is enough.
export const sameCodes = (a: string[], b: string[]): boolean => a.length === b.length && containsAll(a, b);
